import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3000';

let socketInstance = null;
let listenerCount = 0;

function getSocket() {
  if (!socketInstance) {
    socketInstance = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      autoConnect: false,
    });
  }
  return socketInstance;
}

export function useSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [stockUpdates, setStockUpdates] = useState([]);
  const [zoneTransfers, setZoneTransfers] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = getSocket();
    socketRef.current = socket;
    listenerCount++;

    if (!socket.connected) {
      socket.connect();
    }

    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    function onNewTransaction(data) {
      setLastTransaction(data);
    }

    function onStockUpdate(data) {
      setStockUpdates((prev) => [data, ...prev].slice(0, 100));
    }

    function onZoneTransfer(data) {
      setZoneTransfers((prev) => [data, ...prev].slice(0, 50));
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('new-transaction', onNewTransaction);
    socket.on('stock-update', onStockUpdate);
    socket.on('zone-transfer', onZoneTransfer);

    setIsConnected(socket.connected);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('new-transaction', onNewTransaction);
      socket.off('stock-update', onStockUpdate);
      socket.off('zone-transfer', onZoneTransfer);

      listenerCount--;
      if (listenerCount === 0) {
        socket.disconnect();
        socketInstance = null;
      }
    };
  }, []);

  const subscribeToZone = useCallback((zone) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe-zone', zone);
    }
  }, []);

  const unsubscribeFromZone = useCallback((zone) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe-zone', zone);
    }
  }, []);

  return {
    isConnected,
    lastTransaction,
    stockUpdates,
    zoneTransfers,
    subscribeToZone,
    unsubscribeFromZone,
    socket: socketRef.current,
  };
}

export default useSocket;
